import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Card, TextField } from '../components/ui';
import { ApiClientError } from '../api/client';
import { fieldErrorsFromApiError } from '../lib/field-errors';
import logo from '../theme/NeuroEdgeLogo.png';
import { useAuth } from './AuthContext';

type Form = { businessName: string; displayName: string; email: string; password: string };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Mirrors the backend's `registerBody` limits (identity.schemas.ts) so a well-formed
 * submission never needs a round-trip to discover a validation problem. */
function validate(form: Form): Record<string, string> {
  const errors: Record<string, string> = {};
  if (form.businessName.trim().length < 2) {
    errors.businessName = 'Business name must be at least 2 characters.';
  }
  if (form.displayName.trim().length < 1) {
    errors.displayName = 'Enter your name.';
  }
  if (!EMAIL_RE.test(form.email.trim())) {
    errors.email = 'Enter a valid email address.';
  }
  if (form.password.length < 10) {
    errors.password = 'Password must be at least 10 characters.';
  }
  return errors;
}

export function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState<Form>({
    businessName: '',
    displayName: '',
    email: '',
    password: '',
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const set = (key: keyof Form) => (e: { target: { value: string } }) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    setFieldErrors((f) => {
      if (!f[key]) return f;
      const { [key]: _removed, ...rest } = f;
      return rest;
    });
  };

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const errors = validate(form);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setBusy(true);
    try {
      await register(form);
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const serverFieldErrors = fieldErrorsFromApiError(err);
        if (Object.keys(serverFieldErrors).length > 0) {
          setFieldErrors(serverFieldErrors);
          setError('Please fix the highlighted fields.');
        } else {
          setError(err.message);
        }
      } else {
        setError(
          err instanceof ApiClientError && err.httpStatus < 500
            ? err.message
            : 'Could not create the workspace. Please try again.',
        );
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ne-auth">
      <Card className="ne-auth__card">
        <p className="ne-auth__back">
          <Link to="/">← Back to home</Link>
        </p>
        <img src={logo} alt="" className="ne-auth__logo" />
        <h1 className="ne-auth__title">Create a workspace</h1>
        <p className="ne-auth__subtitle">You become its owner.</p>
        <form className="ne-auth__form" onSubmit={onSubmit} noValidate>
          <TextField
            label="Business name"
            required
            value={form.businessName}
            onChange={set('businessName')}
            error={fieldErrors.businessName}
          />
          <TextField
            label="Your name"
            autoComplete="name"
            required
            value={form.displayName}
            onChange={set('displayName')}
            error={fieldErrors.displayName}
          />
          <TextField
            label="Email"
            type="email"
            autoComplete="email"
            required
            value={form.email}
            onChange={set('email')}
            error={fieldErrors.email}
          />
          <TextField
            label="Password"
            type="password"
            autoComplete="new-password"
            required
            minLength={10}
            value={form.password}
            onChange={set('password')}
            error={fieldErrors.password}
          />
          {!fieldErrors.password ? <p className="ne-field__hint">At least 10 characters.</p> : null}
          {error ? (
            <p className="ne-auth__error" role="alert">
              {error}
            </p>
          ) : null}
          <Button type="submit" disabled={busy}>
            {busy ? 'Creating…' : 'Create workspace'}
          </Button>
        </form>
        <p className="ne-auth__foot">
          Already have an account? <Link to="/login">Sign in</Link> ·{' '}
          <Link to="/pricing">See plans &amp; pricing</Link>
        </p>
        <p className="ne-auth__foot">
          <Link to="/privacy">Privacy Policy</Link>
        </p>
      </Card>
    </div>
  );
}
